import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { SMLButton } from '../components/primitives/SMLButton';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { SerializedSocialPost, SocialPlatform } from '../promote/types';
import { SocialPostRow } from './SocialPostRow';
import { socialMessages as messages } from './socialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../promote/Promote.module.scss';
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostsTableProps {
  posts: SerializedSocialPost[];
  /** The channel filter. X shows the "Link clicks" column. */
  platform?: SocialPlatform;
  /** The id of the post in the detail, for the selected row. */
  openPostId?: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError?: ErrorAPIResult['error'];
  onOpen: (post: SerializedSocialPost) => void;
  onLoadMore: () => void;
}

/** The posts table: one row per post, newest first, then the note on the dash and "Load more". */
const SocialPostsTableComponent: React.FC<SocialPostsTableProps> = ({
  posts,
  platform,
  openPostId,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onOpen,
  onLoadMore,
}) => {
  const intl = useIntl();

  // Only X gives link clicks: the column shows for the X filter, or when a row of the page has them
  // (design x-connector e2).
  const showLinkClicks = useMemo(
    () => platform === 'x' || posts.some((post) => post.linkClicks !== null),
    [platform, posts]
  );

  const rows = useMemo(
    () =>
      posts.map((post) => (
        <SocialPostRow
          key={post.id}
          post={post}
          isOpen={post.id === openPostId}
          showLinkClicks={showLinkClicks}
          onOpen={onOpen}
        />
      )),
    [onOpen, openPostId, posts, showLinkClicks]
  );

  return (
    <>
      <div className={styles.postTable}>
        <div className={clsx(styles.postHeadRow, showLinkClicks && styles.postGridLinkClicks, styles.desktopOnly)}>
          <span>{intl.formatMessage(messages.columnPost)}</span>
          <span>{intl.formatMessage(messages.columnType)}</span>
          <span>{intl.formatMessage(messages.columnDate)}</span>
          <span className={styles.postNumber}>{intl.formatMessage(messages.views)}</span>
          <span className={styles.postNumber}>{intl.formatMessage(messages.likes)}</span>
          <span className={styles.postNumber}>{intl.formatMessage(messages.comments)}</span>
          <span className={styles.postNumber}>{intl.formatMessage(messages.shares)}</span>
          <span className={styles.postNumber}>{intl.formatMessage(messages.saves)}</span>
          {showLinkClicks ? <span className={styles.postNumber}>{intl.formatMessage(messages.linkClicks)}</span> : null}
        </div>
        {rows}
      </div>
      {loadMoreError !== undefined && <ErrorAlert error={loadMoreError} />}
      <div className={styles.postFoot}>
        <span>
          <span className={shared.dash}>—</span>&nbsp; {intl.formatMessage(messages.footerMissing)}
        </span>
        {hasMore ? (
          <SMLButton className={styles.postFootButton} disabled={isLoadingMore} onClick={onLoadMore}>
            {intl.formatMessage(messages.loadMore)}
          </SMLButton>
        ) : null}
      </div>
    </>
  );
};

export const SocialPostsTable = TypedMemo(SocialPostsTableComponent);
